import { useQuery } from '@tanstack/react-query'
import { Download, FileSpreadsheet, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { errorMessage } from '@/api/errors'
import { useUploadFile } from '@/api/files'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  downloadTemplate,
  importStatusTone,
  importsQuery,
  useStartStudentImport,
} from '@/features/imports/api'
import { ImportBatchCard } from '@/features/imports/ImportBatchCard'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDateTime, formatNumber, humanize } from '@/utils/format'

const REQUIRED = ['admission_number', 'first_name', 'last_name', 'gender', 'class', 'section']
const OPTIONAL = [
  'date_of_birth',
  'roll_number',
  'admission_date',
  'blood_group',
  'nationality',
  'religion',
  'phone',
  'email',
  'address',
  'guardian_name',
  'guardian_relation',
  'guardian_phone',
  'guardian_email',
  'guardian_occupation',
]

export function StudentImportPage() {
  const { t } = useTranslation('imports')
  const withGuardians = useGuardians()
  const optional = withGuardians ? OPTIONAL : OPTIONAL.filter((c) => !c.startsWith('guardian_'))
  useDocumentTitle('Import students')
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadFile()
  const start = useStartStudentImport()
  const history = useQuery(importsQuery())
  const [selected, setSelected] = useState<number | null>(null)
  const [dragging, setDragging] = useState(false)
  const busy = upload.isPending || start.isPending
  const current = selected ?? history.data?.[0]?.id ?? null

  const onFile = async (file: File | undefined) => {
    if (!file) return
    try {
      const stored = await upload.mutateAsync({ file, category: 'import' })
      const batch = await start.mutateAsync(stored.id)
      setSelected(batch.id)
      toast.info(t('Import queued'), t('{{file}} is being processed in the background.', { file: file.name }))
    } catch (e) {
      toast.error(t('Could not start the import'), errorMessage(e))
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  return (
    <>
      <PageHeader
        title={t('Import students')}
        description={t(
          'Admit many students at once from a CSV file. Rows are processed in the background with the same rules as the admission form.',
        )}
        breadcrumbs={[{ label: t('Students'), to: '/admin/students' }, { label: t('Import') }]}
        homeHref="/admin"
        actions={
          <Button
            variant="secondary"
            leftIcon={<Download />}
            onClick={() =>
              void downloadTemplate().catch((e: unknown) =>
                toast.error(t('Download failed'), errorMessage(e)),
              )
            }
          >
            {t('CSV template')}
          </Button>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-6">
          <Card>
            <CardBody>
              <input
                ref={input}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                aria-label={t('Choose CSV file')}
                onChange={(e) => void onFile(e.target.files?.[0])}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => input.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragging(false)
                  void onFile(e.dataTransfer.files[0])
                }}
                className={cn(
                  'flex w-full flex-col items-center justify-center rounded-card border-2 border-dashed px-6 py-12 text-center transition-colors disabled:opacity-60',
                  dragging
                    ? 'border-brand-400 bg-brand-50/60'
                    : 'border-line-strong hover:border-brand-300 hover:bg-surface-muted',
                )}
              >
                <span className="grid size-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-8 ring-brand-50/50">
                  <Upload className="size-6" aria-hidden="true" />
                </span>
                <span className="mt-4 text-sm font-semibold text-ink">
                  {busy ? t('Uploading…') : t('Drop a CSV file here, or click to choose')}
                </span>
                <span className="mt-1 text-xs text-muted">
                  {t('Comma or semicolon separated, UTF-8, up to {{rows}} rows and 10 MB', {
                    rows: formatNumber(5000),
                  })}
                </span>
              </button>
            </CardBody>
          </Card>
          {current ? (
            <ImportBatchCard key={current} id={current} />
          ) : (
            <Card>
              <EmptyState
                icon={<FileSpreadsheet />}
                title={t('No imports yet')}
                description={t('Download the template, fill it in, and upload it here.')}
                compact
              />
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Columns')} description={t('Headers are matched case-insensitively.')} />
            <CardBody className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-1.5">
                {REQUIRED.map((c) => (
                  <Badge key={c} tone="brand" size="sm">
                    {c}
                  </Badge>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {optional.map((c) => (
                  <Badge key={c} size="sm">
                    {c}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted">
                {t(
                  'Class and section use their names (e.g. “Grade 4”, “A”). Gender accepts male/female/other or M/F. Dates use YYYY-MM-DD.',
                )}
              </p>
            </CardBody>
          </Card>
          {history.data?.length ? (
            <Card>
              <CardHeader title={t('Recent imports')} />
              <ul className="divide-y divide-line">
                {history.data.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(b.id)}
                      aria-current={b.id === current ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-surface-muted',
                        b.id === current && 'bg-brand-50/50',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-ink">{b.fileName}</span>
                        <span className="block text-xs text-muted">
                          {formatDateTime(b.createdAt)} ·{' '}
                          {t('{{value}} admitted', {
                            count: b.importedRows,
                            value: formatNumber(b.importedRows),
                          })}
                        </span>
                      </span>
                      <Badge tone={importStatusTone[b.status]} size="sm">
                        {humanize(b.status)}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  )
}
